import React from "react";

import { Breadcrumb } from "antd";

import { goTo, useAppType, useNavigate } from "utils";

import { Flex, IconSizes } from "components";

type CrumbBase = {
  /** Marks the farm crumb: the header shows the global farm switch in its place while the switch is on. */
  replacedByFarmSwitch?: boolean;
};

type PlainCrumb = CrumbBase & {
  name: string;
  link?: string;
  linkState?: { state?: object };
  truncate?: boolean;
};

type CustomCrumb = CrumbBase & {
  /** Renders this crumb as a custom node (e.g. a dropdown) instead of plain text. */
  content: React.ReactNode;
};

export type Crumb = PlainCrumb | CustomCrumb;

export type BreadcrumbsProps = {
  items: Crumb[];
  className?: string;
  dataTestId?: string;
  size?: IconSizes;
  additionalData?: React.ReactNode;
};

export const Breadcrumbs = ({ items, className, dataTestId, size = "s", additionalData }: BreadcrumbsProps) => {
  const navigate = useNavigate();
  const appType = useAppType();

  // additionalData defaults to an empty fragment across the app when there's nothing to show
  const hasAdditionalData = !!additionalData && !(React.isValidElement(additionalData) && additionalData.type === React.Fragment);

  const itemList = appType.app || appType.mobile ? items.slice(-2) : items;

  const isCustomCrumb = (item: Crumb): item is CustomCrumb => "content" in item;

  const getBreadcrumb = (item: PlainCrumb, idx: number) => (
    <span
      // @ts-expect-error test
      onClick={item.link ? () => goTo(null, navigate, item.link, undefined, item.linkState) : null}
      title={item.truncate ? item.name : undefined}
      className={`breadcrumb ${item.link ? `pointer grey-text paragraph-${size}` : `paragraph-${size}`} ${idx === 0 ? "first-breadcrumb" : ""} ${
        idx === itemList.length - 1 && !hasAdditionalData ? "last-breadcrumb" : ""
      } ${item.truncate ? "breadcrumb-truncated" : ""}`}
    >
      {item.name}
    </span>
  );

  return (
    <div className="breadcrumbs-container" data-testid={dataTestId}>
      <Flex align="center">
        <Breadcrumb
          className={className}
          // @ts-expect-error test
          items={
            itemList?.length
              ? itemList.map((item, idx) => {
                  if (isCustomCrumb(item))
                    return {
                      title: (
                        <Flex align="center" className={idx === itemList.length - 1 ? "last-breadcrumb" : ""}>
                          {item.content}
                        </Flex>
                      ),
                    };
                  if (idx === itemList.length - 1 && hasAdditionalData)
                    return {
                      title: (
                        <Flex align="center" gap="0.5rem">
                          {getBreadcrumb(item, idx)}
                        </Flex>
                      ),
                    };
                  return { title: getBreadcrumb(item, idx) };
                })
              : null
          }
          separator="/"
        />
        {hasAdditionalData && (
          <>
            <span className="breadcrumb-additional-data-separator">/</span>
            <Flex align="center" className="last-breadcrumb">
              {additionalData}
            </Flex>
          </>
        )}
      </Flex>
    </div>
  );
};
